import { useEffect, useRef, useState } from "react";
import {
  Loader2,
  X,
  Save,
  Upload,
  AlertTriangle,
  Plus,
  Trash2,
  ImageIcon,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiErrorDetail } from "../lib/api";

const _uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `uid-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
const _mkInclude = (text = "") => ({ _uid: _uid(), text });

const emptyForm = () => ({
  name: "",
  image: "",
  description: "",
  best_for: "",
  includes: [_mkInclude("")],
  is_published: true,
});

/**
 * Editor for one of the 4 homepage marketing "Catering Packages".
 *
 * Props:
 *   open, onOpenChange
 *   pkg: the package row to edit (required — edit-only, no create flow)
 *   onSaved: () => void
 */
export const PackageEditorModal = ({ open, onOpenChange, pkg, onSaved }) => {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (pkg) {
      setForm({
        name: pkg.name || "",
        image: pkg.image || "",
        description: pkg.description || "",
        best_for: pkg.best_for || "",
        includes: pkg.includes?.length
          ? pkg.includes.map((s) => _mkInclude(s))
          : [_mkInclude("")],
        is_published: pkg.is_published !== false,
      });
    } else {
      setForm(emptyForm());
    }
    setError("");
  }, [open, pkg]);

  const update = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((s) => ({ ...s, [k]: v }));
  };

  const updateInclude = (idx, value) => {
    setForm((s) => {
      const next = [...s.includes];
      next[idx] = { ...next[idx], text: value };
      return { ...s, includes: next };
    });
  };
  const addInclude = () =>
    setForm((s) => ({ ...s, includes: [...s.includes, _mkInclude("")] }));
  const removeInclude = (idx) =>
    setForm((s) => ({
      ...s,
      includes: s.includes.filter((_, i) => i !== idx),
    }));

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError("Image must be 8MB or smaller.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/packages/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const fullUrl = res.data.url.startsWith("http")
        ? res.data.url
        : `${apiBase}${res.data.url}`;
      setForm((s) => ({ ...s, image: fullUrl }));
    } catch (err) {
      setError(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          err?.message ||
          "Upload failed"
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const validate = () => {
    if (!form.name.trim()) return "Name is required.";
    if (!form.description.trim() || form.description.trim().length < 10)
      return "Description must be at least 10 characters.";
    if (!form.image.trim()) return "An image is required (upload or paste a URL).";
    return "";
  };

  const submit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    const includes = form.includes.map((it) => (it.text || "").trim()).filter(Boolean);
    setError("");
    setSaving(true);
    try {
      await api.patch(`/admin/packages/${pkg.id}`, {
        name: form.name,
        image: form.image,
        description: form.description,
        best_for: form.best_for,
        includes,
        is_published: form.is_published,
      });
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e?.message ||
          "Could not save"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="package-editor-modal"
        className="sm:max-w-[720px] max-h-[92vh] overflow-y-auto p-0 rounded-2xl border-none"
      >
        <div className="bg-brand-charcoal text-white px-6 py-5 sticky top-0 z-10">
          <DialogHeader className="space-y-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-gold">
              Edit Package
            </p>
            <DialogTitle className="font-display font-black text-[22px] tracking-tight text-white text-left">
              {form.name || pkg?.name || "Package"}
            </DialogTitle>
            <DialogDescription className="text-[12.5px] text-white/65 text-left">
              Update the photo, headline, description, and bullets. Saves
              instantly to the homepage.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-5 sm:px-6 py-5 space-y-5">
          {/* Image */}
          <section className="rounded-2xl bg-brand-cream/40 border border-black/5 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-black text-[14px] text-brand-charcoal">
                Hero Image
              </h3>
              <button
                type="button"
                data-testid="pkg-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors disabled:opacity-60"
              >
                {uploading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
                {uploading ? "Uploading…" : "Upload Image"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                data-testid="pkg-file-input"
              />
            </div>
            {form.image ? (
              <div
                data-testid="pkg-image-preview"
                className="relative aspect-[16/9] rounded-xl overflow-hidden bg-white border border-black/5"
              >
                <img
                  src={form.image}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="rounded-xl border-2 border-dashed border-black/15 p-8 text-center text-[12.5px] text-brand-charcoal/60">
                <ImageIcon className="h-6 w-6 mx-auto mb-2 text-brand-charcoal/30" />
                No image yet. Upload one or paste a URL below.
              </div>
            )}
            <div className="mt-3">
              <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
                Image URL
              </Label>
              <Input
                data-testid="pkg-image-url"
                value={form.image}
                onChange={update("image")}
                placeholder="https://…"
                className="mt-1.5 h-10 rounded-xl bg-white border-black/10 text-[13px]"
              />
            </div>
          </section>

          {/* Core fields */}
          <section className="rounded-2xl bg-brand-cream/40 border border-black/5 p-4 grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Field label="Name *">
                <Input
                  data-testid="pkg-name"
                  value={form.name}
                  onChange={update("name")}
                  className="h-10 rounded-xl bg-white border-black/10"
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Description *">
                <Textarea
                  data-testid="pkg-description"
                  value={form.description}
                  onChange={update("description")}
                  rows={3}
                  className="rounded-xl bg-white border-black/10"
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Best for">
                <Input
                  data-testid="pkg-best-for"
                  value={form.best_for}
                  onChange={update("best_for")}
                  placeholder="e.g. office lunches, agency events"
                  className="h-10 rounded-xl bg-white border-black/10"
                />
              </Field>
            </div>
          </section>

          {/* Includes bullets */}
          <section className="rounded-2xl bg-brand-cream/40 border border-black/5 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-black text-[14px] text-brand-charcoal">
                Includes (bullets)
              </h3>
              <button
                type="button"
                data-testid="pkg-add-bullet"
                onClick={addInclude}
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 text-brand-charcoal px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:border-brand-teal hover:text-brand-teal"
              >
                <Plus className="h-3.5 w-3.5" />
                Add bullet
              </button>
            </div>
            <ul className="space-y-2" data-testid="pkg-includes-list">
              {form.includes.map((item, idx) => (
                <li key={item._uid} className="flex items-center gap-2">
                  <Input
                    data-testid={`pkg-include-${idx}`}
                    value={item.text}
                    onChange={(e) => updateInclude(idx, e.target.value)}
                    placeholder="e.g. Sauced wings or tenders"
                    className="h-10 rounded-xl bg-white border-black/10 text-[13px]"
                  />
                  <button
                    type="button"
                    data-testid={`pkg-remove-include-${idx}`}
                    onClick={() => removeInclude(idx)}
                    disabled={form.includes.length === 1}
                    className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white border border-black/10 text-brand-charcoal/55 hover:text-red-600 hover:border-red-200 disabled:opacity-30"
                    aria-label="Remove bullet"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <div className="flex items-center gap-2 px-1">
            <input
              id="pkg-publish"
              type="checkbox"
              checked={form.is_published}
              onChange={(e) =>
                setForm((s) => ({ ...s, is_published: e.target.checked }))
              }
              data-testid="pkg-published"
              className="h-4 w-4 accent-brand-teal"
            />
            <label htmlFor="pkg-publish" className="text-[12.5px] text-brand-charcoal/75">
              Published — visible on the homepage
            </label>
          </div>

          {error && (
            <div
              data-testid="pkg-error"
              role="alert"
              className="inline-flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              data-testid="pkg-save-btn"
              onClick={submit}
              disabled={saving || uploading}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-6 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors disabled:opacity-60"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Changes
            </button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-black/10 text-brand-charcoal/70 px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-cream"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
      {label}
    </Label>
    {children}
  </div>
);

export default PackageEditorModal;
